import React, { useEffect, useRef, useState } from "react";
import Icon from "./icons";
import {
  capitalise, emptySkillForm, enabledLabel, formFromSkill, hasCopyOf, isRefused, methodLine, rewriteState, skillFormErrors, skillNote,
  skillPayload, skillReading
} from "../../../lib/artemis/skills";
import { Switch } from "./alertsView";
import { SkillMarkdown } from "./userSkills";

// The hub's Skills section (WEB-7170, Option B): the user's skills as the list, the editor in the centre,
// and Artemis's own methods on the right with Make it mine. The canvas "Artemis Redesign Options", boards
// "B · web prototype (Play)" and "Shared components". Builds on WEB-7124 (userSkills.js, lib/artemis/skills).

// The list column. notice: the last refusal from the server (a 409 at the switch limit), as it wrote it.
export const SkillList = ({ skills, limits, builtIns, selectedId, onSelect, onToggle, onNew, notice, loading }) => (
  <aside className="artemis-panel artemis-hub-list" aria-label="Your skills">
    <div className="artemis-hub-list-head">
      <span className="artemis-hub-list-titles">
        <span className="artemis-hub-list-title">Skills</span>
        <span className="artemis-hub-list-sub">{enabledLabel(skills.filter(s => !isRefused(s)), limits)}</span>
      </span>
      <button type="button" className="artemis-hub-new" title="New skill" aria-label="New skill" onClick={onNew} disabled={skills.length >= limits.MaxSkills}>
        <Icon name="plus" size={18} />
      </button>
    </div>
    {notice && <p className="artemis-dictation-note artemis-hub-skill-notice" role="status">{notice}</p>}
    <div className="artemis-hub-list-body">
      {skills.length === 0 && (
        <p className="artemis-muted artemis-hub-empty">
          {loading ? 'Loading…' : 'Tell Artemis how you hunt, like how you glass or play the wind, and it follows it on later questions. Add one with +, or make one of Artemis’s methods yours.'}
        </p>
      )}
      {skills.map(s => {
        const refused = isRefused(s)
        return (
          <div key={s.Id} className={`artemis-hub-row-wrap ${s.Id === selectedId ? 'artemis-hub-row-on' : ''}`}>
            <button type="button" className="artemis-hub-row" aria-current={s.Id === selectedId || undefined} onClick={() => onSelect(s)}>
              <span className="artemis-hub-row-text">
                <span className={`artemis-hub-row-title ${s.Enabled === false ? 'artemis-hub-row-plain' : ''}`}>{capitalise(s.Title)}</span>
                <span className={`artemis-hub-row-sub ${refused ? 'artemis-hub-row-refused' : ''}`}>{skillNote(s, builtIns)}</span>
              </span>
            </button>
            {/* A refused skill isn't used whatever its switch says, so it has none until it's edited. */}
            {!refused && <span className="artemis-hub-row-switch"><Switch on={s.Enabled !== false} label={`Use ${s.Title}`} onChange={on => onToggle(s, on)} /></span>}
          </div>
        )
      })}
    </div>
  </aside>
)

const Field = ({ label, error, count, max, children }) => (
  <label className="artemis-hub-field">
    <span className="artemis-hub-field-label">
      {label}
      {max ? <span className={`artemis-field-count ${count > max ? 'artemis-field-over' : ''}`}>{count.toLocaleString('en-US')}/{max.toLocaleString('en-US')}</span> : null}
    </span>
    {children}
    {error && <span className="artemis-field-error">{error}</span>}
  </label>
)

// "How Artemis reads it", collapsed: the rewrite Artemis follows (API-3801), "Rewriting…" just after a
// save, or why it was refused. The rewrite is Markdown from the model, shown as plain text.
const HowArtemisReads = ({ skill, savedAt, now }) => {
  const state = rewriteState(skill, savedAt, now)
  if (!state) return null
  const reading = skillReading(skill)
  return (
    <details className="artemis-hub-reads" open={state === 'refused' || undefined}>
      <summary>How Artemis reads it{state === 'rewriting' ? ' · Rewriting…' : ''}</summary>
      {state === 'refused' && <p>Artemis won’t use this as written: it doesn’t read as a hunting method. Edit it and it will be picked up again.</p>}
      {state === 'rewriting' && <p>Artemis is rewriting your words into steps it can follow. This takes a few seconds.</p>}
      {state === 'own' && <p>Artemis follows your own words until its rewrite is ready.</p>}
      {state === 'ready' && (
        <>
          {reading.appliesWhen && <div className="artemis-skill-reading-when"><span className="artemis-skill-reading-label">Uses it when</span><SkillMarkdown text={reading.appliesWhen} /></div>}
          {reading.body && <SkillMarkdown text={reading.body} className="artemis-skill-reading-body" />}
          {reading.tools.length > 0 && (
            <div className="artemis-step-chips" aria-label="Tools it uses">
              {reading.tools.map(t => <span key={t} className="artemis-step-chip">{t}</span>)}
            </div>
          )}
        </>
      )}
    </details>
  )
}

// The centre: the chosen skill (skill), or a new one (draft, a form's values). onSave(payload) resolves
// with the saved skill; onBack returns to the chat the user came from, when they came from one.
export const SkillEditor = ({ skill, draft, limits, builtIns, savedAt, now, onSave, onDelete, onBack }) => {
  const [form, setForm] = useState(() => (skill ? formFromSkill(skill) : { ...emptySkillForm(), ...(draft || {}) }))
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [serverError, setServerError] = useState(null)
  const errors = skillFormErrors(form, limits)
  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }))
  const refused = isRefused(skill)
  const payload = skillPayload(form, skill, limits)
  const unchanged = !!skill && Object.keys(payload).length === 0

  const save = () => {
    setTouched(true)
    if (Object.keys(errors).length || unchanged) return
    setSaving(true)
    setServerError(null)
    onSave(payload).catch(err => setServerError((err && err.userMessage) || 'Couldn’t save that. Try again.')).finally(() => setSaving(false))
  }

  return (
    <div className="artemis-panel artemis-panel-full artemis-hub-editor">
      <header className="artemis-header">
        {onBack && (
          <button type="button" className="artemis-icon-button artemis-header-button" title={onBack.label} aria-label={onBack.label} onClick={onBack.run}>
            <Icon name="back" size={19} />
          </button>
        )}
        <span className="artemis-header-text">
          <span className="artemis-header-title"><span className="artemis-hub-title">{capitalise(form.title.trim()) || (skill ? capitalise(skill.Title) : 'New skill')}</span></span>
          <span className="artemis-header-meta">{skill ? skillNote(skill, builtIns) : form.basedOn ? `A copy of Artemis’s method, yours to change` : 'Not saved yet'}</span>
        </span>
        {refused && <span className="artemis-hub-chip-refused">Not used</span>}
        <button type="button" className="artemis-hub-save" onClick={save} disabled={saving || unchanged}>{saving ? 'Saving…' : 'Save'}</button>
      </header>
      <div className="chat-container artemis-scroll">
        <div className="artemis-hub-skill-form">
          {refused && <p className="artemis-hub-refused-box" role="note">Artemis won’t use this as written: it isn’t a hunting method. Edit it and it will be picked up again.</p>}
          <Field label="Title" error={touched && errors.title} count={form.title.length} max={limits.MaxTitleLength}>
            <input className="artemis-input-field" value={form.title} onChange={set('title')} maxLength={limits.MaxTitleLength} placeholder="Morning elk glassing" />
          </Field>
          <Field label="When Artemis should use it" error={touched && errors.appliesWhen} count={form.appliesWhen.length} max={limits.MaxAppliesWhenLength}>
            <textarea className="artemis-input-field" rows={2} value={form.appliesWhen} onChange={set('appliesWhen')} maxLength={limits.MaxAppliesWhenLength} placeholder="Planning a morning elk hunt" />
          </Field>
          <Field label="How you hunt" error={touched && errors.body} count={form.body.length} max={limits.MaxBodyLength}>
            <textarea className="artemis-input-field" rows={8} value={form.body} onChange={set('body')} maxLength={limits.MaxBodyLength}
              placeholder="Glass north-facing benches from first light, never more than 3 miles from the truck." />
          </Field>
          {serverError && <p className="artemis-field-error" role="alert">{serverError}</p>}
          <HowArtemisReads skill={skill} savedAt={savedAt} now={now} />
          {skill && (confirming
            ? (
              <div className="artemis-hub-rule artemis-hub-rule-confirm" role="group" aria-label={`Delete ${skill.Title}`}>
                <span className="artemis-hub-rule-text"><span className="artemis-hub-rule-title">Delete this skill?</span><span className="artemis-hub-rule-sub">Artemis stops following it straight away.</span></span>
                <button type="button" className="artemis-hub-action artemis-hub-action-go" onClick={() => onDelete(skill)}>Yes, delete</button>
                <button type="button" className="artemis-hub-action" onClick={() => setConfirming(false)}>Keep it</button>
              </div>
            )
            : <button type="button" className="artemis-hub-action artemis-hub-delete" onClick={() => setConfirming(true)}>Delete</button>)}
        </div>
      </div>
    </div>
  )
}

// The right column: Artemis's built-in methods, each with Make it mine (or Added once copied). focus: a
// method a "Method · …" chip opened, outlined.
export const MethodsColumn = ({ builtIns, skills, limits, focus, busy, onMakeMine }) => {
  // The method a chip opened is brought into view, the list being long.
  const focused = useRef(null)
  useEffect(() => { if (focused.current && focused.current.scrollIntoView) focused.current.scrollIntoView({ block: 'center' }) }, [focus, builtIns.length])
  return (
  <aside className="artemis-panel artemis-hub-right artemis-hub-methods" aria-label="Artemis’s methods">
    <div className="artemis-hub-right-scroll">
      <span className="artemis-hub-right-title">Artemis’s methods</span>
      {builtIns.length === 0 && <p className="artemis-muted">Loading…</p>}
      <div className="artemis-hub-watches">
        {builtIns.map(b => (
          <div key={b.Name} className={`artemis-hub-watch ${b.Name === focus ? 'artemis-hub-watch-on' : ''}`} ref={b.Name === focus ? focused : null}>
            <span className="artemis-hub-watch-text">
              <span className="artemis-hub-watch-title">{capitalise(b.Title || b.Name)}</span>
              <span className="artemis-hub-watch-sub">{methodLine(b)}</span>
            </span>
            {hasCopyOf(skills, b.Name)
              ? <span className="artemis-hub-added">Added</span>
              : <button type="button" className="artemis-hub-action artemis-hub-action-edit" disabled={busy === b.Name} onClick={() => onMakeMine(b)}>{busy === b.Name ? 'Opening…' : 'Make it mine'}</button>}
          </div>
        ))}
      </div>
      <p className="artemis-hub-note">
        Make it mine copies the method into your skills, prefilled, so you can change it. {enabledLabel(skills.filter(s => !isRefused(s)), limits)}.
        Or tell Artemis in any chat: “save this as a skill”.
      </p>
    </div>
  </aside>
  )
}
